import { formatGr, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { BottomSheet } from '../components/BottomSheet';
import { ReceiptLine } from '../components/receipts/ReceiptLine';
import { Snackbar, type SnackbarMessage } from '../components/shop/Snackbar';
import { CHIP_CLS, plural, statusChip } from '../components/shop/status';
import { cardCls, primaryBtn, secondaryBtn, sectionTitleCls } from '../components/ui/classes';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonRows } from '../components/ui/Skeleton';
import { chooseReceiptList } from '../data/lidl';
import { quantityLabel, useList, useListItems } from '../data/lists';
import { articleName } from '../data/prices';
import { assignLine, linePaidGr, rejectLine, useReceipt, useReceiptLines, type ReceiptLineView } from '../data/receipts';
import { useStore } from '../data/stores';
import { db } from '../db/db';
import type { ListItemRow, ReceiptRow } from '../db/types';

const MATCHED = new Set(['auto', 'ai', 'confirmed']);

export const receiptDate = (ms: number) =>
  new Date(ms).toLocaleString('pl-PL', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Paragon: kwoty, lista, do której należy, i dopasowanie linii do pozycji (potwierdzanie, przypisywanie). */
export function ReceiptPage() {
  const { id } = useParams();
  const receipt = useReceipt(id);
  const lines = useReceiptLines(id);
  const list = useList(receipt?.listId ?? undefined);
  const items = useListItems(receipt?.listId ?? undefined);
  const store = useStore(receipt?.storeId);
  const [lineSheet, setLineSheet] = useState<ReceiptLineView | null>(null);
  const [listPicker, setListPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<SnackbarMessage | null>(null);
  const snackId = useRef(0);
  const toast = (text: string) => setSnack({ id: ++snackId.current, text });

  if (receipt === undefined || lines === undefined) return <Loading />;
  if (!receipt || receipt.deleted || !id)
    return (
      <>
        <PageHeader title="Paragon" back="/prices" />
        <p className="p-8 text-center text-stone-500">Nie ma takiego paragonu.</p>
      </>
    );

  const itemsById = new Map((items ?? []).map((i) => [i.id, i]));
  const suggested = lines.filter((l) => l.match === 'suggested' && l.listItemId && itemsById.has(l.listItemId));
  const matched = lines.filter((l) => l.listItemId && MATCHED.has(l.match ?? '') && itemsById.has(l.listItemId));
  const used = new Set([...suggested, ...matched].map((l) => l.id));
  const offList = lines.filter((l) => !used.has(l.id));
  const byItem = new Map<Id, ReceiptLineView[]>();
  for (const l of matched) byItem.set(l.listItemId!, [...(byItem.get(l.listItemId!) ?? []), l]);
  const missingItems = (items ?? []).filter((i) => !byItem.has(i.id) && !suggested.some((l) => l.listItemId === i.id));
  const offListGr = offList.reduce((s, l) => s + linePaidGr(l), 0);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast(ok);
    } catch (e) {
      toast(e instanceof Error && e.message ? e.message : 'Nie udało się zapisać');
    } finally {
      setBusy(false);
    }
  };

  const pickList = (listId: Id) => {
    setListPicker(false);
    void run(async () => {
      const r = await chooseReceiptList(receipt.id, listId);
      toast(`Dopasowano ${r.auto + r.ai} ${plural(r.auto + r.ai, 'pozycję', 'pozycje', 'pozycji')}${r.suggested ? `, ${r.suggested} do potwierdzenia` : ''}`);
    });
  };

  return (
    <>
      <PageHeader title="Paragon" back="/prices" />
      <main className="flex flex-1 flex-col gap-5 p-4 pb-8">
        <section className={`${cardCls} p-4`}>
          <p className="text-sm text-stone-500">
            {store?.name ?? receipt.storeName ?? 'Lidl'}
            {receipt.purchasedMs ? ` · ${receiptDate(receipt.purchasedMs)}` : ''}
          </p>
          <p className="mt-1 text-3xl font-bold tabular-nums">{formatGr(receipt.totalGr ?? 0)}</p>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
            {receipt.linesCount ?? lines.length} {plural(receipt.linesCount ?? lines.length, 'pozycja', 'pozycje', 'pozycji')}
            {receipt.savingsGr ? ` · oszczędności ${formatGr(receipt.savingsGr)}` : ''}
            {receipt.depositGr ? ` · kaucja ${formatGr(receipt.depositGr)}` : ''}
          </p>
          <ListLine receipt={receipt} title={list?.title ?? null} onPick={() => setListPicker(true)} />
        </section>

        {suggested.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className={sectionTitleCls}>Do potwierdzenia ({suggested.length})</h2>
            <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
              {suggested.map((l) => (
                <li key={l.id}>
                  <ReceiptLine line={l}>
                    <p className="mt-1 text-sm">
                      To <b>{itemsById.get(l.listItemId!)?.name}</b> z listy?
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button type="button" disabled={busy} onClick={() => void run(() => assignLine(l.id, l.listItemId!))} className={primaryBtn}>
                        Tak
                      </button>
                      <button type="button" disabled={busy} onClick={() => void run(() => rejectLine(l.id))} className={secondaryBtn}>
                        Nie
                      </button>
                    </div>
                  </ReceiptLine>
                </li>
              ))}
            </ul>
          </section>
        )}

        {matched.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className={sectionTitleCls}>Z listy ({byItem.size})</h2>
            <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
              {[...byItem].map(([itemId, ls]) => {
                const item = itemsById.get(itemId)!;
                return (
                  <li key={itemId} className="py-1">
                    <p className="px-4 pt-2 text-sm font-semibold text-stone-500">
                      {item.name}
                      {quantityLabel(item) && <span className="ml-1 font-normal">{quantityLabel(item)}</span>}
                    </p>
                    {ls.map((l) => (
                      <ReceiptLine key={l.id} line={l} onClick={() => setLineSheet(l)} />
                    ))}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {offList.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className={sectionTitleCls}>
              {receipt.listId ? 'Spoza listy' : 'Pozycje'} ({offList.length} · {formatGr(offListGr)})
            </h2>
            <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
              {offList.map((l) => (
                <li key={l.id}>
                  <ReceiptLine line={l} onClick={receipt.listId ? () => setLineSheet(l) : undefined} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {receipt.listId && missingItems.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className={sectionTitleCls}>Na liście, bez paragonu ({missingItems.length})</h2>
            <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
              {missingItems.map((i) => {
                const chip = statusChip(i);
                return (
                  <li key={i.id} className="flex items-center gap-3 px-4 py-3">
                    <span className="min-w-0 flex-1 truncate">{i.name}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${CHIP_CLS[chip.tone]}`}>{chip.text}</span>
                  </li>
                );
              })}
            </ul>
            <p className="px-1 text-xs text-stone-500">Jeśli coś z tego jest jednak na paragonie, dotknij linii w „Spoza listy” i wskaż pozycję.</p>
          </section>
        )}
      </main>

      <LineSheet
        line={lineSheet}
        items={items ?? []}
        onClose={() => setLineSheet(null)}
        onAssign={(itemId) => {
          const l = lineSheet!;
          setLineSheet(null);
          void run(() => assignLine(l.id, itemId));
        }}
        onDetach={() => {
          const l = lineSheet!;
          setLineSheet(null);
          void run(() => rejectLine(l.id));
        }}
      />
      <ListPicker open={listPicker} receipt={receipt} onClose={() => setListPicker(false)} onPick={pickList} />
      <Snackbar message={snack} onDismiss={() => setSnack(null)} offset="calc(env(safe-area-inset-bottom, 0px) + 1rem)" />
    </>
  );
}

function ListLine({ receipt, title, onPick }: { receipt: ReceiptRow; title: string | null; onPick: () => void }) {
  if (receipt.listId)
    return (
      <div className="mt-3 flex items-center gap-2 border-t border-stone-200 pt-3 text-sm dark:border-stone-800">
        <span className="min-w-0 flex-1 truncate">
          Lista:{' '}
          <Link to={`/lists/${receipt.listId}/after`} className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-500">
            {title ?? 'Zakupy'}
          </Link>
        </span>
        <button type="button" onClick={onPick} className="min-h-11 shrink-0 px-2 font-medium text-brand-700 dark:text-brand-500">
          zmień
        </button>
      </div>
    );
  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-stone-200 pt-3 dark:border-stone-800">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        {receipt.listState === 'ambiguous' ? 'Kilka list z tego dnia pasuje do paragonu — wskaż właściwą.' : 'Paragon nie ma listy zakupów.'}
      </p>
      <button type="button" onClick={onPick} className={receipt.listState === 'ambiguous' ? primaryBtn : secondaryBtn}>
        Wybierz listę
      </button>
    </div>
  );
}

function LineSheet({
  line,
  items,
  onClose,
  onAssign,
  onDetach,
}: {
  line: ReceiptLineView | null;
  items: ListItemRow[];
  onClose: () => void;
  onAssign: (itemId: Id) => void;
  onDetach: () => void;
}) {
  const current = line?.listItemId && line.match !== 'rejected' ? items.find((i) => i.id === line.listItemId) : undefined;
  const sorted = [...items].sort((a, b) => Number(a.status !== 'pending') - Number(b.status !== 'pending') || a.position - b.position);
  return (
    <BottomSheet open={!!line} onClose={onClose} title={line ? (line.article ? articleName(line.article) : (line.rawName ?? '')) : undefined}>
      {line && (
        <div className="flex flex-col gap-3">
          {current && (
            <button type="button" onClick={onDetach} className={`${secondaryBtn} text-red-600 dark:text-red-400`}>
              To nie „{current.name}” — odepnij
            </button>
          )}
          <p className="text-sm text-stone-500">{current ? 'Albo przypisz do innej pozycji:' : 'Która to pozycja z listy?'}</p>
          <ul className="flex max-h-[50dvh] flex-col divide-y divide-stone-200 overflow-y-auto rounded-xl border border-stone-200 dark:divide-stone-800 dark:border-stone-800">
            {sorted
              .filter((i) => i.id !== current?.id)
              .map((i) => (
                <li key={i.id}>
                  <button type="button" onClick={() => onAssign(i.id)} className="flex min-h-12 w-full items-center gap-2 px-3 text-left active:bg-stone-100 dark:active:bg-stone-800">
                    <span className="min-w-0 flex-1 truncate">{i.name}</span>
                    {i.status !== 'pending' && <span className="shrink-0 text-xs text-stone-500">{statusChip(i).text}</span>}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </BottomSheet>
  );
}

function ListPicker({ open, receipt, onClose, onPick }: { open: boolean; receipt: ReceiptRow; onClose: () => void; onPick: (listId: Id) => void }) {
  const lists = useLiveQuery(async () => {
    const at = receipt.purchasedMs ?? Date.now();
    return (await db.lists.toArray())
      .filter((l) => !l.deleted && l.id !== receipt.listId)
      .sort((a, b) => Math.abs((a.createdMs ?? 0) - at) - Math.abs((b.createdMs ?? 0) - at))
      .slice(0, 30);
  }, [receipt.purchasedMs, receipt.listId]);
  return (
    <BottomSheet open={open} onClose={onClose} title="Do której listy?">
      <ul className="flex flex-col divide-y divide-stone-200 dark:divide-stone-800">
        {(lists ?? []).map((l) => (
          <li key={l.id}>
            <button type="button" onClick={() => onPick(l.id)} className="flex min-h-12 w-full flex-col items-start justify-center px-1 py-2 text-left active:bg-stone-100 dark:active:bg-stone-800">
              <span className="w-full truncate font-medium">{l.title ?? 'Zakupy'}</span>
              {l.createdMs && <span className="text-xs text-stone-500">{new Date(l.createdMs).toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'long' })}</span>}
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-stone-500">Potrzebny internet — dopasowanie pozycji robi serwer.</p>
    </BottomSheet>
  );
}

function Loading() {
  return (
    <div className="p-4">
      <SkeletonRows count={5} />
    </div>
  );
}
